import type { AppShellNavSection } from '@avila-roffe/components-web';
import {
  Avatar,
  AvatarFallback,
  AppShell as DesignSystemAppShell,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@avila-roffe/components-web';
import { ChevronsUpDown, Globe, LayoutDashboard, LogOut, Settings, UserRound } from 'lucide-react';
import React, { type ReactNode, Suspense, useMemo } from 'react';
import { Link, useLocation, useNavigate } from 'react-router';
import { useAuth } from '../../auth/AuthContext';
import { useCurrentUser } from '../../hooks/useCurrentUser';

// ─── Router link adapter ──────────────────────────────────────────────────────

const RouterLink = React.forwardRef<
  HTMLAnchorElement,
  React.AnchorHTMLAttributes<HTMLAnchorElement>
>(({ href, ...props }, ref) => <Link to={href ?? '#'} ref={ref} {...props} />);
RouterLink.displayName = 'RouterLink';

// ─── Logo mark — replace with app-specific branding when copying ──────────────

function LogoMark() {
  return (
    <div className="flex items-center gap-2.5 px-1 py-0.5">
      <div className="flex size-7 shrink-0 items-center justify-center rounded-md bg-primary text-primary-foreground text-xs font-bold select-none">
        MW
      </div>
      <span className="text-sm font-semibold text-sidebar-foreground">MyTaible Waiter</span>
    </div>
  );
}

// ─── Sidebar user menu ────────────────────────────────────────────────────────

function SidebarUserMenu() {
  const { logout } = useAuth();
  const currentUser = useCurrentUser();
  const navigate = useNavigate();

  const initials = currentUser?.initials ?? 'MW';
  const displayName = currentUser?.displayName ?? '…';
  const email = currentUser?.email;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          aria-label="Open user menu"
          className="flex w-full items-center gap-2.5 rounded-lg px-2 py-2 text-left transition-colors hover:bg-sidebar-accent/50"
        >
          <Avatar size="sm" shape="rounded">
            <AvatarFallback>{initials}</AvatarFallback>
          </Avatar>
          <div className="flex min-w-0 flex-1 flex-col">
            <span className="truncate text-sm font-medium text-foreground">{displayName}</span>
          </div>
          <ChevronsUpDown className="size-4 shrink-0 text-muted-foreground/60" aria-hidden="true" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent side="top" align="start" sideOffset={8} className="w-64">
        <div className="flex items-center gap-3 px-3 py-2">
          <Avatar size="sm" shape="rounded">
            <AvatarFallback>{initials}</AvatarFallback>
          </Avatar>
          <div className="flex min-w-0 flex-col gap-0.5">
            <span className="truncate text-sm font-semibold text-foreground leading-none">
              {displayName}
            </span>
            {email && email !== displayName && (
              <span className="truncate text-xs text-muted-foreground leading-none">{email}</span>
            )}
          </div>
        </div>
        <DropdownMenuSeparator />
        <DropdownMenuItem onSelect={() => navigate('/preferences')}>
          <UserRound className="size-4" />
          My Account
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem onSelect={() => navigate('/settings')}>
          <Settings className="size-4" />
          Settings
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          onSelect={() => void logout()}
          className="text-destructive focus:text-destructive focus:bg-destructive/10"
        >
          <LogOut className="size-4" />
          Sign out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

// ─── AppShell ─────────────────────────────────────────────────────────────────

interface AppShellProps {
  children: ReactNode;
}

const NAV_ITEMS = [
  { label: 'Dashboard', href: '/dashboard', icon: LayoutDashboard },
  { label: 'i18n', href: '/i18n', icon: Globe },
] as const;

export function AppShell({ children }: AppShellProps) {
  const location = useLocation();
  const currentUser = useCurrentUser();

  const nav: AppShellNavSection[] = useMemo(
    () => [
      {
        items: NAV_ITEMS.map(({ label, href, icon }) => ({
          label,
          href,
          icon,
          active: location.pathname === href || location.pathname.startsWith(`${href}/`),
        })),
      },
    ],
    [location.pathname],
  );

  return (
    <Suspense>
      <DesignSystemAppShell
        layout="app-shell-05"
        logo={<LogoMark />}
        nav={nav}
        mobileNav={nav}
        sidebarFooter={<SidebarUserMenu />}
        user={{ name: currentUser?.displayName ?? '' }}
        topbar={{ showSearch: false }}
        linkComponent={RouterLink}
        locationKey={location.key}
      >
        <div className="flex flex-col flex-1 overflow-y-auto gap-4 min-h-0 p-4 sm:p-6">
          {children}
        </div>
      </DesignSystemAppShell>
    </Suspense>
  );
}

export default AppShell;
